export type ImageShortcutAction =
  | 'zoomIn'
  | 'zoomOut'
  | 'toggleFlip'
  | 'rotateRight'
  | 'rotateLeft'
  | 'increaseContrast'
  | 'decreaseContrast'
  | 'toggleInvert'
  | 'restore'
  | 'toggleHelp'

/** `shift` só é considerado em letras; nos demais símbolos o Shift faz parte da própria tecla. */
interface IKeyBinding {
  key: string
  shift?: boolean
}

export interface IShortcutHint {
  label: string
  display: string
}

interface IImageShortcut extends IShortcutHint {
  bindings: IKeyBinding[]
  /** Segurar a tecla repete a ação; nos toggles e na rotação a repetição faria a imagem piscar. */
  repeatable?: boolean
}

/**
 * Fonte única das ações da imagem: rótulo dos botões, tecla, `title` e lista de atalhos.
 * A ordem das chaves é a ordem da lista.
 */
const IMAGE_SHORTCUTS: Record<ImageShortcutAction, IImageShortcut> = {
  zoomIn: {
    label: 'Aumentar zoom',
    display: '+',
    bindings: [{ key: '+' }, { key: '=' }],
    repeatable: true
  },
  zoomOut: {
    label: 'Diminuir zoom',
    display: '-',
    bindings: [{ key: '-' }],
    repeatable: true
  },
  toggleFlip: {
    label: 'Espelhar horizontalmente',
    display: 'H',
    bindings: [{ key: 'h' }]
  },
  rotateLeft: {
    label: 'Girar 90° à esquerda',
    display: 'Shift + R',
    bindings: [{ key: 'r', shift: true }]
  },
  rotateRight: {
    label: 'Girar 90° à direita',
    display: 'R',
    bindings: [{ key: 'r' }]
  },
  decreaseContrast: {
    label: 'Diminuir contraste',
    display: 'Shift + C',
    bindings: [{ key: 'c', shift: true }],
    repeatable: true
  },
  increaseContrast: {
    label: 'Aumentar contraste',
    display: 'C',
    bindings: [{ key: 'c' }],
    repeatable: true
  },
  toggleInvert: {
    label: 'Inverter cores',
    display: 'I',
    bindings: [{ key: 'i' }]
  },
  restore: {
    label: 'Restaurar ajustes',
    display: '0',
    bindings: [{ key: '0' }]
  },
  toggleHelp: {
    label: 'Mostrar atalhos de teclado',
    display: '?',
    bindings: [{ key: '?' }]
  }
}

const isImageShortcutAction = (key: string): key is ImageShortcutAction =>
  key in IMAGE_SHORTCUTS

const SHORTCUT_ACTIONS = Object.keys(IMAGE_SHORTCUTS).filter(
  isImageShortcutAction
)

export const IMAGE_SHORTCUT_HINTS: IShortcutHint[] = SHORTCUT_ACTIONS.map(
  action => ({
    label: IMAGE_SHORTCUTS[action].label,
    display: IMAGE_SHORTCUTS[action].display
  })
)

const isLetter = (key: string) => /^[a-z]$/i.test(key)

const matchesBinding = (
  binding: IKeyBinding,
  key: string,
  shiftKey: boolean
) => {
  if (!isLetter(key)) return binding.key === key
  // Com Caps Lock a letra chega maiúscula sem Shift; o que decide é o `shiftKey`.
  return binding.key === key.toLowerCase() && !!binding.shift === shiftKey
}

type ShortcutKeyEvent = Pick<
  KeyboardEvent,
  'key' | 'shiftKey' | 'ctrlKey' | 'metaKey' | 'altKey' | 'repeat'
>

/** Ignora combinações com Ctrl/⌘/Alt para não sobrepor os atalhos do navegador. */
export const matchShortcut = (
  event: ShortcutKeyEvent
): ImageShortcutAction | null => {
  if (event.ctrlKey || event.metaKey || event.altKey) return null

  const action = SHORTCUT_ACTIONS.find(candidate =>
    IMAGE_SHORTCUTS[candidate].bindings.some(binding =>
      matchesBinding(binding, event.key, event.shiftKey)
    )
  )
  if (!action || (event.repeat && !IMAGE_SHORTCUTS[action].repeatable)) {
    return null
  }

  return action
}

export const getShortcutLabel = (action: ImageShortcutAction) =>
  IMAGE_SHORTCUTS[action].label

/** Rótulo com a tecla, para o `title` dos botões: "Inverter cores (I)". */
export const getShortcutTitle = (action: ImageShortcutAction) =>
  `${IMAGE_SHORTCUTS[action].label} (${IMAGE_SHORTCUTS[action].display})`

// Em `aria-keyshortcuts` o "+" separa teclas; a própria tecla "+" é escrita "Plus".
const toAriaKey = (key: string) => (key === '+' ? 'Plus' : key.toUpperCase())

/** Valor de `aria-keyshortcuts` da ação, no formato da especificação ("Shift+R"). */
export const getAriaKeyShortcuts = (action: ImageShortcutAction) =>
  IMAGE_SHORTCUTS[action].bindings
    .map(({ key, shift }) => `${shift ? 'Shift+' : ''}${toAriaKey(key)}`)
    .join(' ')
